import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { img } from "@/lib/site";

const HeroSlides = ({ slides, alt }) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(id);
  }, [slides.length]);
  return (
    <div className="relative aspect-[16/9] w-full overflow-hidden bg-black" data-testid="collection-hero-slider">
      {slides.map((src, i) => (
        <motion.img
          key={src}
          src={src}
          alt={`${alt} ${i + 1}`}
          data-testid={`collection-hero-slide-${i}`}
          className="absolute inset-0 h-full w-full object-cover"
          initial={false}
          animate={{ opacity: i === index ? 1 : 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        />
      ))}
      <div className="absolute inset-x-0 bottom-0 z-10 flex items-center gap-3 px-5 pb-5" data-testid="collection-hero-dots">
        {slides.map((src, i) => (
          <button
            key={src}
            type="button"
            onClick={() => setIndex(i)}
            data-testid={`collection-hero-dot-${i}`}
            aria-label={`Show image ${i + 1}`}
            className={`h-[3px] transition-all duration-500 ${
              i === index ? "w-10 bg-white" : "w-5 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export const CollectionHero = ({ crumbs, eyebrow, title, intro, hero, heroSlides, testid = "collection-hero" }) => (
  <div data-testid={testid}>
    <Breadcrumbs items={crumbs} />
    <section className="edge pt-8 pb-12 md:pt-12">
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h1 className="mt-5 text-4xl font-light leading-[1.02] tracking-editorial md:text-6xl">
        {title}
      </h1>
      {intro && (
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
          {intro}
        </p>
      )}
    </section>
    {heroSlides ? (
      <section className="edge pb-16 md:pb-24">
        <div className="overflow-hidden">
          <HeroSlides slides={heroSlides} alt={title} />
        </div>
      </section>
    ) : hero ? (
      <section className="edge pb-16 md:pb-24">
        <div className="overflow-hidden bg-secondary/40">
          <img src={img(hero, 2400)} alt={title} className="aspect-[16/9] w-full object-cover" />
        </div>
      </section>
    ) : null}
  </div>
);

// Reusable tile grid for hub pages
export const HubTiles = ({ items, columns = "md:grid-cols-3" }) => (
  <section className="edge pb-24 md:pb-32" data-testid="hub-tiles">
    <div className={`grid grid-cols-1 gap-x-8 gap-y-14 ${columns}`}>
      {items.map((c) => (
        <Link
          key={c.to}
          to={c.to}
          data-testid={`hub-tile-${c.to.split("/").pop()}`}
          className="group block"
        >
          <div className="aspect-[4/5] overflow-hidden bg-secondary/40">
            <img
              src={img(c.image, 1200)}
              alt={c.label}
              className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
            />
          </div>
          <div className="mt-6 flex items-baseline justify-between">
            <div>
              <h3 className="text-2xl font-light tracking-editorial">{c.label}</h3>
              {c.caption && <p className="mt-2 text-sm text-muted-foreground">{c.caption}</p>}
            </div>
            <span className="text-lg transition-transform duration-500 group-hover:translate-x-1">→</span>
          </div>
        </Link>
      ))}
    </div>
  </section>
);
